import React, { useEffect, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./pincity.css";
import logoimage from "./assets/ajlogo2026.png";
import backimage from "./assets/backimage2.png";

mapboxgl.accessToken =
  "pk.eyJ1IjoiYWpqZXJ1c2FsZW0iLCJhIjoiY2l4b2t3N2lyMDAxMzJxcHMwZ3IzOWN1OSJ9.cxZW0ZgGPFDZpc_-JtRBxg";

const TOTAL_TEAMS = 48;

const worldCupCountries = new Set([
  "Western Sahara","Algeria","Egypt","Democratic Republic of the Congo","Ghana","Tunisia",
  "South Africa","Ivory Coast","Senegal","Cape Verde","Iran","Iraq","Jordan","Morocco",
  "Qatar","Saudi Arabia","Turkey","Uzbekistan","Austria","Belgium",
  "Bosnia and Herzegovina","Croatia","Czechia","England","Scotland","France","Germany",
  "Netherlands","Norway","Portugal","Spain","Sweden","Switzerland","Argentina","Brazil",
  "Canada","Colombia","Curaçao","Ecuador","Haiti","Mexico","Panama","Paraguay",
  "United States","Uruguay","Australia","Japan","New Zealand","South Korea"
]);

const UK_ISO_MAP = { Scotland: "SCO", Wales: "WLS", "Northern Ireland": "NIR", England: "ENG" };
const UK_NATIONS = new Set(["Scotland", "Wales", "Northern Ireland", "England"]);

// When any key country is clicked/found, highlight all values too (treated as one entry)
// The canonical name (key) is what gets stored in foundRef
const COUNTRY_ALIASES = {
  "Western Sahara": ["Morocco", "Western Sahara"],
  "Morocco":        ["Morocco", "Western Sahara"],
};

function getZoomLevel(width = window.innerWidth) {
  if (width <= 380) return 0.9;
  if (width <= 480) return 1.1;
  if (width <= 768) return 1.5;
  if (width <= 960) return 1.8;
  if (width <= 1120) return 2.0;
  return 2.4;
}

function isMobile() {
  return window.innerWidth <= 768;
}

// Build Mapbox filter from sets in one place, expanding aliases
function buildFilter(nameSet) {
  // Expand any alias groups (e.g. "Western Sahara" → ["Morocco","Western Sahara"])
  const expanded = new Set();
  for (const name of nameSet) {
    const group = COUNTRY_ALIASES[name];
    if (group) group.forEach(n => expanded.add(n));
    else expanded.add(name);
  }
  const nonUK = [...expanded].filter(c => !UK_NATIONS.has(c));
  const uk    = [...expanded].filter(c =>  UK_NATIONS.has(c)).map(c => UK_ISO_MAP[c]);
  return ["any",
    nonUK.length ? ["in", "name_en",           ...nonUK] : ["==", "name_en", "__never__"],
    uk.length    ? ["in", "iso_3166_1_alpha_3", ...uk]   : ["==", "name_en", "__never__"],
  ];
}

// Tiny audio helpers — created once, outside the component
let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  // Resume if suspended (browser autoplay policy)
  if (_audioCtx.state === "suspended") _audioCtx.resume();
  return _audioCtx;
}

function playCheer(enabled) {
  if (!enabled) return;
  try {
    const ctx = getAudioCtx();
    [[523, 0], [659, 0.05], [784, 0.1]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator(), gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime + delay);
      gain.gain.setValueAtTime(0.25, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.4);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.4);
    });
  } catch (_) {}
}

function playBoo(enabled) {
  if (!enabled) return;
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator(), gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(220, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.35);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch (_) {}
}

function playFinalWhistle(enabled) {
  if (!enabled) return;
  try {
    const ctx = getAudioCtx();
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(2800, ctx.currentTime);
    const lfo = ctx.createOscillator(), lfoG = ctx.createGain();
    lfo.frequency.value = 18; lfoG.gain.value = 30;
    lfo.connect(lfoG); lfoG.connect(o.frequency);
    lfo.start(ctx.currentTime); lfo.stop(ctx.currentTime + 0.7);
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.015);
    g.gain.setValueAtTime(0.18, ctx.currentTime + 0.55);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.65);
    o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.7);
  } catch (_) {}
}

export default function GeoGame() {
  const mapContainer = useRef(null);
  const mapRef       = useRef(null);
  const mapReadyRef  = useRef(false); // true once layers are loaded

  // Game state in refs (used inside map event handler to avoid stale closures)
  const foundRef   = useRef(new Set());
  const wrongRef   = useRef(new Set());
  const lockedRef  = useRef(false);
  const statusRef  = useRef("start");
  const clicksRef  = useRef(0);
  const scoreRef   = useRef(0);
  const soundRef   = useRef(true);

  // Marker pool — reuse DOM elements instead of creating new ones each click
  const markerPoolRef = useRef([]);
  const activeMarkers = useRef([]);

  // React state (UI only)
  const [status,    setStatus]    = useState("start");
  const [score,     setScore]     = useState(0);
  const [clicks,    setClicks]    = useState(0);
  const [last,      setLast]      = useState("");
  const [timeLeft,  setTimeLeft]  = useState(180);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // ─── helpers ────────────────────────────────────────────────────────────────

  function setGameStatus(next) { statusRef.current = next; setStatus(next); }

  // Get or create a pooled marker element
  function getMarkerEl(isCorrect) {
    let el = markerPoolRef.current.pop();
    if (!el) {
      el = document.createElement("div");
      el.style.cssText = "width:12px;height:12px;border-radius:50%;border:2px solid white;";
    }
    el.style.background = isCorrect ? "green" : "red";
    return el;
  }

  function flushMarkers() {
    activeMarkers.current.forEach(m => m.remove());
    activeMarkers.current = [];
    // markers returned to pool automatically when re-used above
  }

  function resetMapFilters() {
    if (!mapRef.current || !mapReadyRef.current) return;
    const never = ["==", "name_en", "__never__"];
    mapRef.current.setFilter("correct", never);
    mapRef.current.setFilter("wrong",   never);
  }

  function resetGame() {
    foundRef.current  = new Set();
    wrongRef.current  = new Set();
    lockedRef.current = false;
    clicksRef.current = 0;
    scoreRef.current  = 0;
    setScore(0); setClicks(0); setLast(""); setTimeLeft(180);
    flushMarkers();
    resetMapFilters();
  }

  const startGame = useCallback(() => { resetGame(); setGameStatus("playing"); }, []);

  function endGame() {
    if (statusRef.current === "won" || statusRef.current === "gameover") return;
    setGameStatus("gameover");
    playFinalWhistle(soundRef.current);
    lockedRef.current = true;
  }

  function winGame() {
    if (statusRef.current === "won" || statusRef.current === "gameover") return;
    setGameStatus("won");
    playFinalWhistle(soundRef.current);
    lockedRef.current = true;
  }

  function toggleSound() {
    const next = !soundRef.current;
    soundRef.current = next;
    setSoundEnabled(next);
  }

  // ─── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (status !== "playing") return;
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(id); if (statusRef.current === "playing") endGame(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [status]);

  // ─── Resize (stable, set once) ───────────────────────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) mapRef.current.setZoom(getZoomLevel());
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);  // empty dep — handler uses closure-free mapRef

  // ─── Map Init (runs once) ─────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current) return;

    const mobile = isMobile();

    const map = new mapboxgl.Map({
      container:   mapContainer.current,
      style:       "mapbox://styles/ajjerusalem/cmo85egz2002r01qv2l4w9b5e",
      center:      [15, 20],          // slightly higher center looks better on portrait mobile
      zoom:        getZoomLevel(),
      minZoom:     1,
      maxZoom:     5.7,
      interactive: true,
      // ── Performance flags ──────────────────────────────────────────────────
      antialias:           false,     // cheaper on mobile
      refreshExpiredTiles: false,     // don't re-fetch already-loaded tiles
      fadeDuration:        0,         // instant tile appearance = less jank
      // ── Mobile UX ─────────────────────────────────────────────────────────
      dragRotate:   false,            // disable 3-finger rotate — common accidental trigger
      touchPitch:   false,            // disable tilt gesture
      pitchWithRotate: false,
      cooperativeGestures: mobile,    // on mobile: require two fingers to pan, avoids page-scroll conflict
    });

    mapRef.current = map;

    // Only show navigation on desktop (buttons are too small and cluttered on mobile)
    if (!mobile) {
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-left");
    }

    map.on("load", () => {
      map.addSource("countries", {
        type:    "vector",
        url:     "mapbox://mapbox.country-boundaries-v1",
        // Limit to a single zoom range — avoids loading high-detail tiles we never need
        minzoom: 0,
        maxzoom: 6,
      });

      // Single base layer — queryRenderedFeatures only needs one layer now
      map.addLayer({
        id:     "countries-base",
        type:   "fill",
        source: "countries",
        "source-layer": "country_boundaries",
        paint: { "fill-color": "#eeeeee", "fill-opacity": 0 },
        // Only render the disputed=false, worldview=US features (reduces feature count ~60%)
        filter: ["all",
          ["==", ["get", "disputed"], "false"],
          ["any", ["==", "all", ["get", "worldview"]], ["in", "US", ["get", "worldview"]]],
        ],
      });

      const never = ["==", "name_en", "__never__"];

      map.addLayer({
        id:     "correct",
        type:   "fill",
        source: "countries",
        "source-layer": "country_boundaries",
        paint:  { "fill-color": "#81C784", "fill-opacity": 0.5 },
        filter: never,
      });

      map.addLayer({
        id:     "wrong",
        type:   "fill",
        source: "countries",
        "source-layer": "country_boundaries",
        paint:  { "fill-color": "#E57373", "fill-opacity": 0.5 },
        filter: never,
      });

      mapReadyRef.current = true;
    });

    // ── Click / tap handler ──────────────────────────────────────────────────
    map.on("click", (e) => {
      if (statusRef.current !== "playing") return;
      if (lockedRef.current) return;
      if (!mapReadyRef.current) return;

      // Query only the base layer — one layer is faster than three
      const features = map.queryRenderedFeatures(e.point, { layers: ["countries-base"] });
      if (!features.length) return;

      let name = features[0].properties?.name_en;
      if (!name) return;

      // UK nation splitting by lat/lng
      if (name === "United Kingdom") {
        const { lng, lat } = e.lngLat;
        if      (lat > 55.3)                      name = "Scotland";
        else if (lat > 54.5 && lng < -5.5)        name = "Northern Ireland";
        else if (lat > 51.5 && lng < -3)          name = "Wales";
        else                                       name = "England";
      }

      // Normalise aliases to a single canonical name stored in found/wrong sets.
      // "Western Sahara" and "Morocco" are both treated as the "Morocco" entry.
      const CANONICAL = { "Western Sahara": "Morocco" };
      if (CANONICAL[name]) name = CANONICAL[name];

      if (foundRef.current.has(name) || wrongRef.current.has(name)) return;

      const isCorrect = worldCupCountries.has(name);
      setLast(name);

      clicksRef.current += 1;
      setClicks(clicksRef.current);

      // ── Marker (pooled) ────────────────────────────────────────────────────
      const el = getMarkerEl(isCorrect);
      const marker = new mapboxgl.Marker(el)
        .setLngLat([e.lngLat.lng, e.lngLat.lat])
        .addTo(map);
      activeMarkers.current.push(marker);

      // ── Score ─────────────────────────────────────────────────────────────
      if (isCorrect) {
        foundRef.current.add(name);
        scoreRef.current += 1;
        setScore(scoreRef.current);
        playCheer(soundRef.current);
      } else {
        wrongRef.current.add(name);
        scoreRef.current = Math.max(0, scoreRef.current - 1);
        setScore(scoreRef.current);
        playBoo(soundRef.current);
      }

      // ── Map filter update (batched, single call per click) ─────────────────
      // Use requestAnimationFrame so it doesn't block the tap response
      requestAnimationFrame(() => {
        if (!mapRef.current || !mapReadyRef.current) return;
        mapRef.current.setFilter("correct", buildFilter(foundRef.current));
        mapRef.current.setFilter("wrong",   buildFilter(wrongRef.current));
      });

      // ── Win / lose checks ─────────────────────────────────────────────────
      if (foundRef.current.size >= TOTAL_TEAMS) { winGame(); return; }
      if (clicksRef.current    >= TOTAL_TEAMS)  { endGame(); return; }
    });

    // Clean up on unmount
    return () => { map.remove(); mapRef.current = null; mapReadyRef.current = false; };
  }, []);

  // ─── Share strings ───────────────────────────────────────────────────────────
  const tweetText = `Can you identify all 48 teams in World Cup 2026? I scored ${score}/48. @AJEnglish @AJLabs - play here: https://aljazeera.com/football2026quiz`;
  const whatsText = `Can you identify all 48 teams in World Cup 2026? I scored ${score}/48. @AJEnglish @AJLabs — play here: https://aljazeera.com/football2026quiz`;
  const tweeturl  = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
  const whatsurl  = `whatsapp://send?text=${encodeURIComponent(whatsText)}`;

  const ShareLinks = () => (
    <section className="combined-share__links text-center">
      <a className="combined-share__social-link" target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook"
        href="https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Faljazeera.com%2Ffootball2026quiz%2F">
        <svg className="icon icon--facebook icon--social icon--24" viewBox="0 0 20 20" aria-hidden="true"><title>facebook</title><path className="icon-main-color" d="M10,0 C4.47714844,0 0,4.47714844 0,10 C0,14.9912891 3.65685547,19.1283203 8.4375,19.8785157 L8.4375,12.890625 L5.8984375,12.890625 L5.8984375,10 L8.4375,10 L8.4375,7.796875 C8.4375,5.290625 9.93042967,3.90625 12.2146484,3.90625 C13.3087305,3.90625 14.453125,4.1015625 14.453125,4.1015625 L14.453125,6.5625 L13.1921484,6.5625 C11.9499023,6.5625 11.5625,7.33333984 11.5625,8.12416016 L11.5625,10 L14.3359375,10 L13.8925782,12.890625 L11.5625,12.890625 L11.5625,19.8785157 C16.3431445,19.1283203 20,14.9912891 20,10 C20,4.47714844 15.5228516,0 10,0 Z"/></svg>
      </a>
      <a className="combined-share__social-link" target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter" href={tweeturl}>
        <svg className="icon icon--twitter icon--social icon--24" viewBox="0 0 26 24" aria-hidden="true"><title>twitter</title><path className="icon-main-color" fillRule="evenodd" d="m.063 0 10.039 13.237L0 24h2.273l8.845-9.423L18.263 24H26L15.397 10.018 24.799 0h-2.273l-8.145 8.678L7.801 0H.062Zm3.344 1.652H6.96l15.695 20.696h-3.554L3.407 1.652Z"/></svg>
      </a>
      <a className="combined-share__social-link" target="_blank" rel="noopener noreferrer" aria-label="Share on Whatsapp" href={whatsurl}>
        <svg className="icon icon--whatsapp icon--social icon--24" viewBox="0 0 20 20" aria-hidden="true"><title>whatsapp</title><path className="icon-main-color" d="M19 9.74a9.54 9.54 0 0 1-9.33 9.74 9 9 0 0 1-4.51-1.21L0 20l1.68-5.23a10 10 0 0 1-1.33-5A9.53 9.53 0 0 1 9.62 0 9.54 9.54 0 0 1 19 9.74zM14.33 12c-.05-.1-.21-.16-.44-.28s-1.35-.7-1.56-.78-.36-.12-.51.12a11.45 11.45 0 0 1-.73.94c-.13.16-.26.18-.49.06a6.28 6.28 0 0 1-1.84-1.19 7 7 0 0 1-1.27-1.66.35.35 0 0 1 .1-.49c.1-.11.23-.28.34-.42a1.6 1.6 0 0 0 .23-.4.42.42 0 0 0 0-.41c-.06-.12-.51-1.3-.7-1.78s-.39-.4-.52-.4H6.5a.83.83 0 0 0-.61.3 2.74 2.74 0 0 0-.8 2A4.63 4.63 0 0 0 6 10.05a9.54 9.54 0 0 0 3.9 3.61c2.33 1 2.33.64 2.75.6a2.33 2.33 0 0 0 1.54-1.14 2 2 0 0 0 .14-1.12z"/></svg>
      </a>
    </section>
  );

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* TOP BAR */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        paddingTop: 2, paddingRight: 10, paddingLeft: 10,
        background: "rgba(255,255,255,0.5)", fontWeight: 600, zIndex: 999,
      }}>
        <div className="scoring-points">Score: {score}/48</div>
        <div style={{ textAlign: "center" }}>
          <a href="https://aljazeera.com" target="_blank" rel="noopener noreferrer">
            <img src={logoimage} alt="Al Jazeera" style={{ width: "110px" }} />
          </a>
        </div>
        <div className="scoring-points">
          Clicks: {clicks}/48&nbsp;
          <button onClick={toggleSound} aria-label={soundEnabled ? "Mute" : "Unmute"}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", padding: 0, lineHeight: 1 }}
            title={soundEnabled ? "Sound on" : "Sound off"}>
            {soundEnabled ? "🔊" : "🔇"}
          </button>
        </div>
      </div>

      <div className="timeremain">Time: {timeLeft}s</div>
      <div className="rules">
        <p className="smallertext"><strong>Rules</strong>: You have 48 guesses per game. Each incorrect guess costs 1 point. Spin the globe 🌍 and tap 📍 a country to select it.</p>
      </div>

      {/* Map — always rendered so Mapbox GL stays mounted */}
      <div ref={mapContainer} style={{ flex: 1 }} />

      {status === "start" && (
        <Overlay backimage={backimage}>
          <p className="mapquiz">MAP QUIZ</p>
          <div className="homebox">
            <h1 className="h1 openbox">Can you identify all 48 teams in World Cup 2026?</h1>
            <p className="centretext openbox smalltext">
              Spin the globe 🌍 and tap 📍 a country to select it. You have{" "}
              <strong className="yellowtext">3 minutes to make 48 guesses.</strong> Each mistake costs 1 point.
            </p>
            <button className="button-text" onClick={startGame}>START</button>
          </div>
        </Overlay>
      )}

      {status === "gameover" && (
        <Overlay backimage={backimage}>
          <div className="gameoverbox">
            <h2 className="h1">Game Over</h2>
            <h1 className="h1">You scored: {score}/48</h1>
            <p className="text-centre">SHARE</p>
            <ShareLinks />
            <button className="button-text" onClick={startGame}>Play Again</button>
          </div>
        </Overlay>
      )}

      {status === "won" && (
        <Overlay backimage={backimage}>
          <div className="gameoverbox">
            <h2 className="h1">🎉 Congratulations!</h2>
            <h1 className="h1">You found all 48 teams!</h1>
            <p className="centretext">With <strong>{timeLeft}s remaining</strong></p>
            <ShareLinks />
            <button className="button-text" onClick={startGame}>Play Again</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function Overlay({ children, backimage }) {
  return (
    <div className="overstyle" style={{
      position: "absolute", inset: 0,
      backgroundColor: "#F8BE03",
      backgroundImage: `url(${backimage})`,
      backgroundPosition: "50% 50%",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", color: "white", zIndex: 99, gap: 10,
    }}>
      {children}
    </div>
  );
}
